import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { DEFAULT_ROUTE } from '@app/nav/navigation';
import { supabaseAuthService } from './supabaseAuth';
import type { AuthContextValue, AuthService, AuthStatus, AuthUser } from './types';

const AuthContext = createContext<AuthContextValue | null>(null);

export function normalizeHashRoute(route: string): string {
  return route.split('?')[0] || '#/login';
}

/**
 * Detects and clears Supabase signup confirmation callback hashes.
 * This is called after Supabase has processed the confirmation and established a session.
 * Returns true if the hash was cleared.
 */
export function clearSignupConfirmationHash(): boolean {
  if (typeof window === 'undefined') return false;

  const hash = window.location.hash;
  if (!hash) return false;

  // Check if hash contains Supabase signup confirmation callback parameters
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const isSignupConfirmation = params.get('type') === 'signup' && params.has('access_token');

  if (isSignupConfirmation) {
    // Clear the hash by navigating to login
    window.location.hash = '#/login';
    return true;
  }

  return false;
}

/**
 * Detects and clears Supabase auth error hashes (e.g., from consumed/expired
 * confirmation tokens) when the user is already authenticated.
 * Returns true if the hash was cleared.
 */
export function clearAuthErrorHash(): boolean {
  if (typeof window === 'undefined') return false;

  const hash = window.location.hash;
  if (!hash) return false;

  // Check if hash contains Supabase auth error parameters
  const errorParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const hasAuthError =
    errorParams.has('error') ||
    errorParams.has('error_code') ||
    errorParams.has('error_description');

  if (hasAuthError) {
    // Clear the hash by navigating to the default route
    window.location.hash = '#/sales/invoices';
    return true;
  }

  return false;
}

export function isRecoveryHashRoute(route: string): boolean {
  if (
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('auth') === 'reset'
  ) {
    return true;
  }

  const nestedFragment = route.indexOf('#', 1);
  if (nestedFragment !== -1) {
    const nestedParams = new URLSearchParams(route.slice(nestedFragment + 1));
    if (
      nestedParams.get('type') === 'recovery' ||
      nestedParams.has('access_token') ||
      nestedParams.has('refresh_token')
    ) {
      return true;
    }
  }

  const normalized = normalizeHashRoute(route);
  if (normalized !== '#/reset-password') {
    return false;
  }

  const search = route.includes('?') ? route.slice(route.indexOf('?') + 1) : '';
  const params = new URLSearchParams(search);
  return (
    params.get('type') === 'recovery' ||
    Boolean(params.get('access_token')) ||
    Boolean(params.get('refresh_token'))
  );
}

export function normalizeSupabaseRecoveryCallback(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const nestedFragment = window.location.hash.indexOf('#', 1);
  if (
    nestedFragment === -1 ||
    normalizeHashRoute(window.location.hash.slice(0, nestedFragment)) !== '#/reset-password'
  ) {
    return false;
  }

  const callbackParameters = window.location.hash.slice(nestedFragment + 1);
  const params = new URLSearchParams(callbackParameters);
  if (
    params.get('type') !== 'recovery' &&
    !params.has('access_token') &&
    !params.has('refresh_token')
  ) {
    return false;
  }

  const callbackUrl = new URL(window.location.href);
  callbackUrl.searchParams.set('auth', 'reset');
  callbackUrl.hash = callbackParameters;
  window.history.replaceState(window.history.state, '', callbackUrl);
  return true;
}

normalizeSupabaseRecoveryCallback();

/**
 * Detects a Supabase signup confirmation callback at module load time.
 * Stores a flag in sessionStorage so we can redirect to #/login after
 * the Supabase client processes the callback and establishes the session.
 */
function detectSignupConfirmation(): void {
  if (typeof window === 'undefined') return;

  const hash = window.location.hash;
  if (!hash) return;

  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const isSignupConfirmation = params.get('type') === 'signup' && params.has('access_token');

  if (isSignupConfirmation) {
    // Store flag for later redirect after session is established
    sessionStorage.setItem('signup_confirmed', 'true');
  }
}

detectSignupConfirmation();

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [state, setState] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [justConfirmed, setJustConfirmed] = useState(false);
  const recoveryEventObserved = useRef(false);

  useEffect(() => {
    let active = true;

    const syncFromSession = async () => {
      try {
        const session = await supabaseAuthService.getCurrentSession();
        if (!active) return;
        if (recoveryEventObserved.current) return;
        const nextUser = session ? { id: session.userId, email: session.email } : null;
        setUser(nextUser);
        setState(session ? 'authenticated' : 'unauthenticated');
      } catch (caught) {
        if (!active) return;
        setUser(null);
        setState('unauthenticated');
        setError('Unable to restore your session.');
      }
    };

    let unsubscribe = () => {};
    try {
      unsubscribe = supabaseAuthService.onAuthStateChange((nextState, nextUser) => {
        if (!active) return;
        if (nextState === 'recovering') {
          recoveryEventObserved.current = true;
          setUser(null);
          setState('recovering');
          setError(null);
          return;
        }
        setUser(nextUser);
        if (nextState === 'loading') {
          setState('loading');
          return;
        }
        setState(nextState);
        setError(null);

        // If we just got authenticated and the URL has a signup confirmation callback,
        // clear the tokens from the URL and mark as just confirmed.
        if (nextState === 'authenticated') {
          const hadSignupConfirmation = clearSignupConfirmationHash();
          const hadStoredConfirmation = sessionStorage.getItem('signup_confirmed') === 'true';
          sessionStorage.removeItem('signup_confirmed');

          if (hadSignupConfirmation || hadStoredConfirmation) {
            setJustConfirmed(true);
          }
        }
      });
    } catch (caught) {
      setUser(null);
      setState('unauthenticated');
      setError(caught instanceof Error ? caught.message : 'Authentication is not configured.');
      return () => {
        active = false;
        unsubscribe();
      };
    }

    void syncFromSession();

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const service: AuthService = {
      signUp: async (email, password) => {
        try {
          const result = await supabaseAuthService.signUp(email, password);
          setError(null);
          return result;
        } catch (caught) {
          const message = caught instanceof Error ? caught.message : 'Unable to create account.';
          setError(message);
          throw caught;
        }
      },
      signIn: async (email, password) => {
        try {
          setError(null);
          const session = await supabaseAuthService.signIn(email, password);
          setUser({ id: session.userId, email: session.email });
          setState('authenticated');
          setJustConfirmed(false); // Clear justConfirmed on manual sign in
          if (window.location.hash !== DEFAULT_ROUTE) {
            window.location.hash = DEFAULT_ROUTE;
          }
          return session;
        } catch (caught) {
          const message = caught instanceof Error ? caught.message : 'Unable to sign in.';
          setError(message);
          throw caught;
        }
      },
      signOut: async () => {
        try {
          setError(null);
          await supabaseAuthService.signOut();
          // Do NOT delete the active-business pointer. The local IndexedDB is
          // per-browser, not per-account: the business rows, outbox, and cursors
          // all persist across sign-out and remain valid for re-sign-in on the
          // same browser. Deleting the pointer caused the welcome screen to appear
          // every time a returning user signed back in, even with the same account.
          //
          // Cross-account safety is handled at the sync layer, NOT here:
          //  - SyncCoordinator checks `authorisedBusinessIds` before running sync.
          //  - SyncSettingsScreen blocks syncing a business the account does not own.
          // So keeping the pointer cannot send another account's data to the cloud.
          //
          // The backup-folder handle is kept for the SAME reason, and is NOT part
          // of the account. It is a device setting: "this device saves backups to
          // that folder". Deleting it on sign-out made the saved folder vanish
          // from Settings after the next sign-in, so automatic backup silently
          // read as "Off — choose a folder" and the scheduler never re-armed.
          // Cross-account safety is enforced where the data is, not by discarding
          // the setting:
          //  - `validateManifestCompatibility` and `runDiskBackup` refuse to write
          //    to a folder whose manifest is owned by another account.
          //  - The engine never picks or re-points a folder on its own.
          setUser(null);
          setState('unauthenticated');
          setJustConfirmed(false);
          if (window.location.hash !== '#/login') {
            window.location.hash = '#/login';
          }
        } catch (caught) {
          const message = caught instanceof Error ? caught.message : 'Unable to sign out.';
          setError(message);
          throw caught;
        }
      },
      getCurrentSession: () => supabaseAuthService.getCurrentSession(),
      getCurrentUser: () => supabaseAuthService.getCurrentUser(),
      requestPasswordReset: async (email) => {
        try {
          setError(null);
          await supabaseAuthService.requestPasswordReset(email);
        } catch (caught) {
          const message =
            caught instanceof Error ? caught.message : 'Unable to send password reset email.';
          setError(message);
          throw caught;
        }
      },
      updatePassword: async (password) => {
        try {
          setError(null);
          await supabaseAuthService.updatePassword(password);
          recoveryEventObserved.current = false;
          setUser(null);
          setState('unauthenticated');
          setJustConfirmed(false);
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.delete('auth');
          window.history.replaceState(window.history.state, '', currentUrl);
        } catch (caught) {
          const message =
            caught instanceof Error ? caught.message : 'Unable to update your password.';
          setError(message);
          throw caught;
        }
      },
      confirmPasswordReset: async (password) => {
        try {
          setError(null);
          await supabaseAuthService.confirmPasswordReset(password);
          recoveryEventObserved.current = false;
          setUser(null);
          setState('unauthenticated');
          setJustConfirmed(false);
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.delete('auth');
          window.history.replaceState(window.history.state, '', currentUrl);
        } catch (caught) {
          const message = caught instanceof Error ? caught.message : 'Unable to reset password.';
          setError(message);
          throw caught;
        }
      },
      onAuthStateChange: (listener) => supabaseAuthService.onAuthStateChange(listener),
    };

    return {
      ...service,
      state,
      user,
      error,
      justConfirmed,
      clearError: () => setError(null),
    };
  }, [state, user, error, justConfirmed]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
